/**
 * State Report Models (Backend API)
 *
 * TypeScript interfaces matching the backend StateReport model for API communication.
 * These interfaces support real API communication for state compliance reports.
 *
 * Reference: /agent-os/specs/2026-01-16-state-reports/spec.md
 */

// ============================================
// STATUS TYPE
// ============================================

/**
 * State Report status values matching backend enum
 */
export type StateReportStatus = 'draft' | 'generating' | 'ready' | 'submitted' | 'failed';

// ============================================
// STATE REPORT OPTIONS
// ============================================

/**
 * Options for state report generation
 * Controls which sections are included in the PDF
 */
export interface StateReportOptions {
  includeChemicalDetails: boolean;
  includeApplicationRates: boolean;
  includeFieldMaps: boolean;
  includeWeatherConditions: boolean;
}

/**
 * Default options for state reports (all enabled)
 */
export const DEFAULT_STATE_REPORT_OPTIONS: StateReportOptions = {
  includeChemicalDetails: true,
  includeApplicationRates: true,
  includeFieldMaps: true,
  includeWeatherConditions: true
};

// ============================================
// STATE REPORT INTERFACE
// ============================================

/**
 * State Report model matching backend response structure
 * This is the complete model returned from the API
 */
export interface StateReport {
  id: string;
  organizationId: string;
  name: string;
  state: string; // 2-letter state code (required)
  reportType: string; // 'all', 'pesticide', 'fertilizer', 'seeding', 'other'
  status: StateReportStatus;
  reportUrl?: string;
  downloadUrl?: string; // Pre-signed URL for download (only present when fetching single report)
  startDate: string;
  endDate: string;
  options: StateReportOptions;
  generationProgress: number;
  recordCount?: number;
  fileSize?: string;
  submittedAt?: string;
  submittedTo?: string;
  errorMessage?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

// ============================================
// API REQUEST/RESPONSE INTERFACES
// ============================================

/**
 * Request body for creating a new state report
 */
export interface CreateStateReportRequest {
  name: string;
  state: string; // 2-letter state code (required)
  startDate: string;
  endDate: string;
  reportType?: string; // defaults to 'all'
  options: StateReportOptions;
}

/**
 * Request body for updating a state report
 */
export interface UpdateStateReportRequest {
  name?: string;
  options?: StateReportOptions;
}

/**
 * Request body for submitting a state report
 */
export interface SubmitStateReportRequest {
  email: string;
}

/**
 * Paginated list response for state reports
 */
export interface StateReportListResponse {
  reports: StateReport[];
  total: number;
  page: number;
  limit: number;
}

/**
 * API response wrapper
 */
export interface StateReportApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

// ============================================
// WEBSOCKET EVENT PAYLOADS
// ============================================

/**
 * WebSocket event payload for state-report:progress
 */
export interface StateReportProgressEvent {
  reportId: string;
  progress: number;
  status: 'generating';
}

/**
 * WebSocket event payload for state-report:complete
 */
export interface StateReportCompleteEvent {
  reportId: string;
  status: 'ready';
  url: string;
  recordCount: number;
  fileSize: string;
}

/**
 * WebSocket event payload for state-report:failed
 */
export interface StateReportFailedEvent {
  reportId: string;
  status: 'failed';
  error: string;
}

/**
 * Union type for all state report WebSocket events
 */
export type StateReportWebSocketEvent =
  | StateReportProgressEvent
  | StateReportCompleteEvent
  | StateReportFailedEvent;

// ============================================
// REPORT TYPE OPTIONS
// ============================================

/**
 * Available state report types
 */
export const STATE_REPORT_TYPE_VALUES = [
  { value: 'all', label: 'All Applications' },
  { value: 'pesticide', label: 'Pesticide Applications' },
  { value: 'fertilizer', label: 'Fertilizer Applications' },
  { value: 'seeding', label: 'Seeding Applications' },
  { value: 'other', label: 'Other Applications' }
] as const;

export type StateReportType = typeof STATE_REPORT_TYPE_VALUES[number]['value'];

// ============================================
// US STATES LIST
// ============================================

/**
 * US States for dropdown selection
 */
export const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' }
] as const;

export type USStateCode = typeof US_STATES[number]['value'];
